import { useState, useRef, useEffect } from 'react'
import { isEmpty } from 'lodash'
import { toast } from '@/components/toasts/toast.helpers'

import { SECTION_TYPES } from './AutomationInsightsReport'

export const ANALYSIS_STATES = {
  idle: 'idle',
  loading: 'loading',
  complete: 'complete',
}

const INITIAL_STATE = {
  state: ANALYSIS_STATES.idle,
  progress: 0,
  message: '',
  report: null,
  error: '',
}

export const useAutomationInsights = ({ apiUrl, username, organizationid, token }) => {
  const [analysisState, setAnalysisState] = useState(INITIAL_STATE)
  const abortControllerRef = useRef(null)
  const stateRef = useRef(null)

  useEffect(() => {
    stateRef.current = analysisState
  }, [analysisState])

  const disconnect = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const fail = (message) => {
    console.error(message)
    toast.error(message)
    setAnalysisState((prevState) => ({
      ...prevState,
      state: ANALYSIS_STATES.idle,
      error: message,
      progress: 0,
      message: '',
    }))
    disconnect()
  }

  const handleStreamMessage = (msg) => {
    switch (msg.type) {
      case 'keepalive':
        // Backend heartbeat event; intentionally ignored.
        break

      case 'status':
        setAnalysisState((prevState) => ({
          ...prevState,
          progress: typeof msg.progress === 'number' ? msg.progress : prevState.progress,
          message: msg.message || prevState.message,
        }))
        break

      case 'complete':
        setAnalysisState((prevState) => ({
          ...prevState,
          state: ANALYSIS_STATES.complete,
          progress: 100,
          message: msg.message || '',
          report: {
            sections: msg.data?.report?.sections || [],
            callCount: msg.data?.call_count || 0,
          },
        }))
        disconnect()
        break

      case 'error':
        fail(msg.message || 'Analysis failed')
        break

      default:
        console.warn('Unknown message type:', msg)
    }
  }

  const parseAndHandleMessage = (rawMessage) => {
    const trimmedMessage = rawMessage.trim()
    if (!trimmedMessage) {
      return
    }

    try {
      const parsedMessage = JSON.parse(trimmedMessage)
      handleStreamMessage(parsedMessage)
    } catch (error) {
      console.warn('Failed to parse message:', trimmedMessage, error)
    }
  }

  const startAnalysis = async (payload) => {
    disconnect()

    abortControllerRef.current = new AbortController()

    setAnalysisState((prevState) => ({
      ...prevState,
      state: ANALYSIS_STATES.loading,
      message: 'Connecting...',
      progress: 0,
      report: null,
      error: '',
    }))

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-type': 'web',
          username,
          organizationid,
          token,
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      })

      if (!response.ok) {
        fail(`HTTP ${response.status}: ${response.statusText}`)
        return
      }

      if (!response.body) {
        fail('Streaming response body unavailable')
        return
      }

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
      let buffer = ''
      let streamDone = false

      while (!streamDone) {
        const { done, value } = await reader.read()

        if (done) {
          if (buffer.trim()) {
            parseAndHandleMessage(buffer)
          }
          streamDone = true
        } else {
          buffer += value
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          lines.forEach(parseAndHandleMessage)
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError' && stateRef.current?.state === ANALYSIS_STATES.loading) {
        fail(error.message || 'Connection error')
      }
    }
  }

  const resetAnalysis = () => {
    disconnect()
    setAnalysisState(INITIAL_STATE)
  }

  const stopAnalysis = () => {
    disconnect()
    setAnalysisState(INITIAL_STATE)
  }

  useEffect(() => {
    return () => disconnect()
  }, [])

  return {
    analysisState: analysisState.state,
    analysisProgress: analysisState.progress,
    analysisMessage: analysisState.message,
    analysisReport: analysisState.report,
    analysisError: analysisState.error,
    startAnalysis,
    stopAnalysis,
    resetAnalysis,
  }
}

// Convert structured sections to markdown string for PDF export
export const convertSectionsToMarkdown = (sections) => {
  const parts = []

  sections.forEach((section) => {
    if (section.type === SECTION_TYPES.EXECUTIVE_SUMMARY) {
      parts.push(`# ${section.title}`)
      parts.push('## Executive Summary')
      parts.push(section.content.methodology)

      if (section.content.key_findings) {
        parts.push('\n### Key Findings')
        parts.push(section.content.key_findings)
      }

      if (section.content.recommendations) {
        parts.push('\n### Recommendations')
        parts.push(section.content.recommendations)
      }
    } else if (section.type === SECTION_TYPES.OVERALL_ASSESSMENT) {
      parts.push(`\n## ${section.title}`)
      parts.push(section.content)
    } else if (
      section.type === SECTION_TYPES.CALL_SEGMENT_OPPORTUNITIES ||
      section.type === SECTION_TYPES.CALL_TYPE_OPPORTUNITIES
    ) {
      parts.push(`\n## ${section.title}`)

      if (!isEmpty(section.opportunities)) {
        parts.push(`*${section.description}*`)
        section.opportunities.forEach((opp) => {
          parts.push(`\n### ${opp.name}`)
          parts.push(`**Description:** ${opp.description}`)

          if (section.type === SECTION_TYPES.CALL_SEGMENT_OPPORTUNITIES) {
            parts.push(`**Applies to:** ${opp.applies_to}`)
          } else {
            parts.push(`**Frequency:** ${opp.frequency}`)
            if (opp.can_fully_automate === true) {
              parts.push('**Can fully automate:** Yes')
            } else if (opp.can_fully_automate === false) {
              parts.push('**Can fully automate:** No')
              parts.push(`**Automatable portion:** ${opp.automatable_portion}`)
            }
          }

          if (!isEmpty(opp.voice_agent_steps)) {
            parts.push('\n**Voice Agent Steps:**')
            opp.voice_agent_steps.forEach((step) => {
              parts.push(`- ${step}`)
            })
          }

          parts.push(`**Handoff Trigger:** ${opp.handoff_trigger}`)
          parts.push(`**Handoff Context:** ${opp.handoff_context}`)

          if (!isEmpty(opp.required_apis)) {
            parts.push(`**Required APIs:** ${opp.required_apis.join(', ')}`)
          }

          if (!isEmpty(opp.challenges)) {
            parts.push('\n**Challenges:**')
            opp.challenges.forEach((challenge) => {
              parts.push(`- ${challenge}`)
            })
          }
        })
      } else {
        parts.push(section.empty_message || 'No opportunities identified.')
      }
    }
  })

  return parts.join('\n\n').trim()
}

/**
 * Inline styles for PDF export (must be self-contained)
 * - Iframe srcdoc cannot access external CSS files so styles must be embedded inline
 */
export const getExportStyles = () => {
  return `
    body{font-family:"Inter","Segoe UI",Arial,sans-serif;color:#111827;margin:32px;line-height:1.5}
    .ai-export .header{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
    .ai-export .title{font-size:24px;font-weight:700;color:#0f172a}
    .ai-export .date{color:#475569;font-size:14px}
    .ai-export .logo{height:32px}
    .ai-export .summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;margin-bottom:24px}
    .ai-export .summary-card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;background:#f8fafc}
    .ai-export .summary-card .label{color:#64748b;font-size:12px;text-transform:uppercase;letter-spacing:0.04em}
    .ai-export .summary-card .value{color:#0f172a;font-size:16px;font-weight:600;margin-top:6px}
    .ai-export h2{page-break-before:always;color:#0f172a;margin-top:24px}
    .ai-export h2:first-of-type{page-break-before:auto}
    .ai-export h3{color:#0f172a;margin-top:16px}
    .ai-export table{width:100%;border-collapse:collapse;margin:16px 0;font-size:14px}
    .ai-export th,.ai-export td{border:1px solid #e5e7eb;padding:8px;text-align:left}
    .ai-export th{background:#f8fafc;font-weight:600}
    .ai-export ul{padding-left:18px}
    .ai-export p{orphans:3;widows:3}
    @media print{body{margin:0.5in}}
  `
}
